import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  NgbTypeaheadModule,
  NgbTypeahead,
  NgbHighlight,
} from "@ng-bootstrap/ng-bootstrap";
import { Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { Tag } from "../../../api/models/tag";

const noop = () => {};
const CREATE_PREFIX = '\0create:';

export const TAG_INPUT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TagInputComponent),
  multi: true,
};

@Component({
  selector: "app-tag-input",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    NgbTypeaheadModule,
    NgbHighlight,
  ],
  providers: [TAG_INPUT_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tag-input-container">
      <div class="input-group">
        <input
          type="text"
          class="form-control"
          [ngbTypeahead]="searchTags"
          [resultTemplate]="tagResultTemplate"
          [editable]="true"
          [focusFirst]="false"
          (selectItem)="onSelectItem($event)"
          (keydown.enter)="onEnter($event)"
          (keydown.,)="onDelimiter($event)"
          (blur)="onBlur()"
          [(ngModel)]="inputModel"
          [placeholder]="placeholder | translate"
        />
      </div>

      @if (selectedTags().length > 0) {
        <div class="mt-2">
          @for (tag of selectedTags(); track tag) {
            <span
              class="badge badge-primary mr-2 mb-2 p-2 d-inline-flex align-items-center"
            >
              {{ tag }}
              <button
                class="btn btn-sm btn-link text-white p-0 ml-1"
                (click)="removeTag(tag)"
                type="button"
              >
                <fa-icon [icon]="faTimes" class="small"></fa-icon>
              </button>
            </span>
          }
        </div>
      }
    </div>

    <ng-template #tagResultTemplate let-r="result" let-t="term">
      @if (isCreateHint(r)) {
        <span class="create-tag-hint">
          {{ 'Add "{{tag}}"' | translate: { tag: extractCreateTerm(r) } }}
        </span>
      } @else {
        <ngb-highlight [result]="r" [term]="t"></ngb-highlight>
      }
    </ng-template>
  `,
  styles: [
    `
      .tag-input-container .badge {
        font-weight: 500;
        font-size: 0.875rem;
        transition: all 0.15s ease-in-out;
      }

      .tag-input-container .badge-primary:hover {
        opacity: 0.85;
      }

      .tag-input-container .btn-link:hover {
        text-decoration: none;
      }

      :host ::ng-deep .dropdown-item:has(.create-tag-hint) {
        border-top: 1px solid #e9ecef;
      }

      .create-tag-hint {
        font-style: italic;
        color: #6c757d;
      }
    `,
  ],
})
export class TagInputComponent implements ControlValueAccessor {
  @Input() suggestions: Tag[] = [];
  @Input() placeholder = "Type to search or add a tag";
  @Input() allowCreate = true;
  @Output() tagCreated = new EventEmitter<string>();

  @ViewChild(NgbTypeahead) typeaheadInstance?: NgbTypeahead;

  readonly faTimes = faTimes;

  selectedTags = signal<string[]>([]);
  inputModel = "";

  private justSelected = false;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (value: string[]) => void = noop;

  searchTags = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map((term) => {
        if (!term || term.length < 1) return [];
        const lower = term.toLowerCase();
        const selected = this.selectedTags();
        const matches = this.suggestions
          .filter(
            (tag) =>
              tag.name?.toLowerCase().includes(lower) &&
              !selected.includes(tag.name!)
          )
          .map((tag) => tag.name!)
          .slice(0, 10);

        const exactMatchExists = this.suggestions.some(
          (tag) => tag.name?.toLowerCase() === lower
        );

        if (this.allowCreate && !exactMatchExists && !selected.includes(term)) {
          matches.push(CREATE_PREFIX + term);
        }

        return matches;
      })
    );

  isCreateHint(item: string): boolean {
    return item.startsWith(CREATE_PREFIX);
  }

  extractCreateTerm(item: string): string {
    return item.slice(CREATE_PREFIX.length);
  }

  onSelectItem(event: any): void {
    event.preventDefault();
    this.justSelected = true;
    let tagName = event.item as string;
    const isNew = this.isCreateHint(tagName);
    if (isNew) {
      tagName = this.extractCreateTerm(tagName);
    }
    this.addTag(tagName);
    if (isNew) {
      this.tagCreated.emit(tagName);
    }
    this.inputModel = "";
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.commitInput();
  }

  onDelimiter(event: Event): void {
    event.preventDefault();
    this.commitInput();
  }

  onBlur(): void {
    if (this.justSelected) {
      this.justSelected = false;
      return;
    }
    this.commitInput();
    this.onTouchedCallback();
  }

  private commitInput(): void {
    const text = this.inputModel?.trim();
    if (!text) return;

    const selected = this.selectedTags();
    if (selected.includes(text)) {
      this.inputModel = "";
      return;
    }

    const match = this.suggestions.find(
      (tag) => tag.name?.toLowerCase() === text.toLowerCase()
    );

    if (match) {
      this.addTag(match.name!);
    } else if (this.allowCreate) {
      this.addTag(text);
      this.tagCreated.emit(text);
    }

    this.inputModel = "";
  }

  removeTag(tagName: string): void {
    const newTags = this.selectedTags().filter((t) => t !== tagName);
    this.selectedTags.set(newTags);
    this.onChangeCallback(newTags);
    this.onTouchedCallback();
  }

  private addTag(tagName: string): void {
    const current = this.selectedTags();
    if (!current.includes(tagName)) {
      const newTags = [...current, tagName];
      this.selectedTags.set(newTags);
      this.onChangeCallback(newTags);
      this.onTouchedCallback();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string[] | null): void {
    this.selectedTags.set(value ?? []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }
}
